import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

import { locationFilterOptions } from '@/entities/location/@x/auth';
import { isUuid } from '@/shared/lib/validators';

import type { Location } from './types';

import { useAuthStore } from './auth-store';

export type JournalKey = 'payments' | 'shifts' | 'stock' | 'tire-storages' | 'invoices';
/** Id локации или `'all'`. */
export type JournalLocationScope = string;
export const ALL_LOCATIONS = 'all';

/** Склад — место операций «здесь»: по умолчанию текущая локация. Остальные журналы — все локации. */
const CURRENT_LOCATION_BY_DEFAULT: ReadonlySet<JournalKey> = new Set(['stock']);

/**
 * Область журналов вкладки: по умолчанию все доступные локации (склад — текущая), выбранная
 * локация держится до смены локации вкладки. Только память вкладки.
 */
export const useJournalLocationScopeStore = defineStore('journalLocationScope', () => {
  const auth = useAuthStore();
  const scopes = ref<Partial<Record<JournalKey, JournalLocationScope>>>({});

  watch(
    () => auth.currentLocation?.id,
    () => {
      scopes.value = {};
    },
  );

  const scopeFor = (key: JournalKey): JournalLocationScope | null =>
    scopes.value[key] ?? (CURRENT_LOCATION_BY_DEFAULT.has(key) ? (auth.currentLocation?.id ?? null) : ALL_LOCATIONS);
  const setScope = (key: JournalKey, scope: JournalLocationScope): void => {
    scopes.value = { ...scopes.value, [key]: scope };
  };

  return { scopeFor, setScope };
});

/**
 * Область одного журнала: выбранная локация или все доступные вкладке, всегда явным списком id.
 * `urlScope` — значение из адреса (`all` или UUID): ссылка на журнал запоминает выбор.
 */
export function useJournalLocationScope(key: JournalKey, { urlScope }: { urlScope?: () => unknown } = {}) {
  const store = useJournalLocationScopeStore();
  const auth = useAuthStore();
  const scope = computed(() => store.scopeFor(key) ?? ALL_LOCATIONS);
  const locationIds = computed<string[]>(() =>
    scope.value === ALL_LOCATIONS ? auth.availableLocations.map((location) => location.id) : [scope.value],
  );
  const setScope = (value: JournalLocationScope): void => store.setScope(key, value);

  if (urlScope) {
    watch(
      urlScope,
      (value) => {
        if (typeof value === 'string' && (value === ALL_LOCATIONS || isUuid(value))) setScope(value);
      },
      { immediate: true },
    );
  }

  return {
    scope,
    locationIds,
    /** Журнал охватывает несколько локаций: при одной доступной «все» равно ей самой. */
    isAll: computed(() => scope.value === ALL_LOCATIONS && auth.availableLocations.length > 1),
    setScope,
    /** `v-model` выбора локации журнала. */
    model: computed({ get: () => scope.value, set: setScope }),
    options: computed(() => journalLocationOptions(auth.availableLocations)),
  };
}

export type UseJournalLocationScopeReturn = ReturnType<typeof useJournalLocationScope>;

export function journalLocationOptions(locations: Location[]): { value: JournalLocationScope; label: string }[] {
  return [{ value: ALL_LOCATIONS, label: 'Все локации' }, ...locationFilterOptions(locations)];
}
